import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { inputCls, btnPrimary } from "@/pages/Auth";
import { EASE } from "@/components/motion";

export default function InviteAccept() {
  const { token } = useParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "", gender: "L", phone: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get(`/public/invitations/${token}`)
      .then((r) => { setInvite(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); })
      .catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/public/invitations/${token}/accept`, {
        ...form, phone: form.phone || null,
      });
      setUser(data);
      toast.success(`Selamat bergabung, ${data.name}! NIG Anda: ${data.nig}`);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="invite-accept-page">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1579975194233-8a4c97b11f1b?auto=format&fit=crop&w=1600&q=75" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900/90 to-indigo-900/80" />
        <Link to="/" data-testid="invite-home-link" className="relative flex items-center"><Logo dark className="h-9" /></Link>
        <div className="relative">
          <p className="text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">Selamat<br />melayani<span className="text-gradient">.</span></p>
          <p className="mt-5 text-slate-300 max-w-md">Anda diundang menjadi bagian dari tim pengurus. Atur kata sandi Anda untuk masuk ke Portal OPC.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10"><Logo className="h-8" /></Link>
          {error ? (
            <div data-testid="invite-error" className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center">
              <p className="text-2xl font-black text-slate-900">Undangan tidak berlaku</p>
              <p className="mt-3 text-slate-500">{error}</p>
              <Link to="/login" className={`${btnPrimary} mt-6 inline-flex`} data-testid="invite-to-login">Ke Halaman Masuk</Link>
            </div>
          ) : !invite ? (
            <div className="flex items-center gap-3 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Memuat undangan…</div>
          ) : (
            <>
              <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1.5" data-testid="invite-role-badge"><ShieldCheck className="w-3.5 h-3.5" /> {invite.role_label}</span>
              <h1 className="text-4xl font-black tracking-tight text-slate-900 mt-4">Terima Undangan</h1>
              <p className="mt-2 text-slate-500">Anda diundang sebagai pengurus <strong>{invite.church}</strong> untuk email <strong>{invite.email}</strong>.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
                <input data-testid="invite-name-input" className={inputCls} placeholder="Nama lengkap" value={form.name} onChange={set("name")} required />
                <input data-testid="invite-password-input" type="password" minLength={6} className={inputCls} placeholder="Buat password (min. 6 karakter)" value={form.password} onChange={set("password")} required />
                <div className="grid grid-cols-2 gap-3" data-testid="invite-gender-group">
                  {[["L", "Pria"], ["P", "Wanita"]].map(([v, l]) => (
                    <button type="button" key={v} data-testid={`invite-gender-${v}`} onClick={() => setForm({ ...form, gender: v })} className={`rounded-xl py-3 font-bold border transition active:scale-95 ${form.gender === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
                  ))}
                </div>
                <input data-testid="invite-phone-input" className={inputCls} placeholder="No. WhatsApp (opsional)" value={form.phone} onChange={set("phone")} />
                <button data-testid="invite-submit-button" disabled={loading} className={`${btnPrimary} w-full`}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Masuk sebagai Pengurus <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
